import RegistrationResendCountdown, {
  createRegistrationResendHandlers
} from './registration_resend_countdown.mjs'
import { isUnhandledFailure, normalizeOtp, registrationOtpLength } from './registration_submission.mjs'

const registrationHistorySnapshots = new Map()
const registrationHistoryReloadKey = 'registration-history-reload'
const registrationRecoveryMessage = 'We couldn’t connect. Check your internet connection and try again.'
const registrationTransientClasses = ['is-sending-email']
const registrationSendingLabel = 'Sending code…'
const registrationVerifyingLabel = 'Verifying…'
const registrationVerifiedLabel = 'Email verified'
const registrationCreatingLabel = 'Creating your account…'
// The tab title follows the step, so a user switching back from their inbox finds the right tab.
const registrationStepTitles = { verify: 'Check your inbox · SignWith', complete: 'Finish your profile · SignWith' }

export default class extends HTMLElement {
  // data-step-settled mirrors this, so system specs can wait for a step to finish.
  get stepSettled () {
    return this.settled
  }

  set stepSettled (value) {
    this.settled = value
    this.toggleAttribute('data-step-settled', value)
  }

  connectedCallback () {
    if (this.initialized) return

    this.initialized = true
    this.stepSettled = true
    this.landingTitle = this.dataset.state === 'email' ? document.title : 'SignWith'
    this.trackedStates = new Set()
    this.onSubmit = this.handleSubmit.bind(this)
    this.onBeforeFrameRender = this.handleBeforeFrameRender.bind(this)
    this.onFrameLoad = this.handleFrameLoad.bind(this)
    this.onFrameRender = this.handleFrameRender.bind(this)
    this.onSubmitEnd = this.handleSubmitEnd.bind(this)
    this.onFetchRequestError = this.handleFetchRequestError.bind(this)
    this.onFrameMissing = this.handleFrameMissing.bind(this)
    this.onPageShow = this.handlePageShow.bind(this)
    this.onBeforeCache = this.handleBeforeCache.bind(this)
    this.onPopState = this.handlePopState.bind(this)
    this.onVisibilityChange = this.handleVisibilityChange.bind(this)
    this.onUnhandledRejection = this.handleUnhandledRejection.bind(this)
    this.onBeforeStreamRender = this.handleBeforeStreamRender.bind(this)
    this.onInput = this.handleInput.bind(this)
    this.addEventListener('submit', this.onSubmit, true)
    this.addEventListener('input', this.onInput)
    this.addEventListener('turbo:before-frame-render', this.onBeforeFrameRender)
    this.addEventListener('turbo:frame-load', this.onFrameLoad)
    this.addEventListener('turbo:frame-render', this.onFrameRender)
    this.addEventListener('turbo:submit-end', this.onSubmitEnd)
    this.addEventListener('turbo:fetch-request-error', this.onFetchRequestError)
    this.addEventListener('turbo:frame-missing', this.onFrameMissing)
    window.addEventListener('pageshow', this.onPageShow)
    window.addEventListener('popstate', this.onPopState)
    document.addEventListener('turbo:before-cache', this.onBeforeCache)
    document.addEventListener('turbo:before-stream-render', this.onBeforeStreamRender)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    window.addEventListener('unhandledrejection', this.onUnhandledRejection)
    // Turbo restores Back/Forward pages from a snapshot captured when the frame
    // submission started, so a restored element can carry mid-submit state.
    this.resetTransientState()
    this.saveHistorySnapshot(this.dataset.state)
    this.initializeState()
  }

  disconnectedCallback () {
    this.initialized = false
    this.resendCountdown?.stop()
    window.clearTimeout(this.emailSubmitTimer)
    window.clearTimeout(this.settleTimer)
    window.clearTimeout(this.historyRestoreTimer)
    window.clearTimeout(this.networkRecoveryTimer)
    this.verificationRevalidationController?.abort()
    this.removeEventListener('submit', this.onSubmit, true)
    this.removeEventListener('input', this.onInput)
    this.removeEventListener('turbo:before-frame-render', this.onBeforeFrameRender)
    this.removeEventListener('turbo:frame-load', this.onFrameLoad)
    this.removeEventListener('turbo:frame-render', this.onFrameRender)
    this.removeEventListener('turbo:submit-end', this.onSubmitEnd)
    this.removeEventListener('turbo:fetch-request-error', this.onFetchRequestError)
    this.removeEventListener('turbo:frame-missing', this.onFrameMissing)
    window.removeEventListener('pageshow', this.onPageShow)
    window.removeEventListener('popstate', this.onPopState)
    document.removeEventListener('turbo:before-cache', this.onBeforeCache)
    document.removeEventListener('turbo:before-stream-render', this.onBeforeStreamRender)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    window.removeEventListener('unhandledrejection', this.onUnhandledRejection)
  }

  handleSubmit (event) {
    const form = event.target.closest('form')
    if (form?.matches('.registration-complete-form')) {
      this.showProfileSubmission(form, event.submitter)
      return
    }

    const isEmailStep = form?.querySelector('input[name="step"][value="email"]')
    if (!isEmailStep || this.dataset.state !== 'email' || form.dataset.sending === 'true') return

    event.preventDefault()
    this.saveHistorySnapshot('email')
    form.dataset.sending = 'true'
    form.setAttribute('aria-busy', 'true')

    const submitter = event.submitter || form.querySelector('[type="submit"]')
    if (submitter) {
      submitter.dataset.originalLabel = submitter.value || submitter.textContent
      if ('value' in submitter) submitter.value = registrationSendingLabel
      else submitter.textContent = registrationSendingLabel
      submitter.disabled = true
    }

    // Nothing moves while the code is being sent: the page changes in one
    // View Transition once the response arrives (transitionStep), so there is
    // never an empty screen waiting on the network.
    this.classList.add('is-sending-email')
    // requestSubmit is ignored while this submit event is still dispatching.
    this.emailSubmitTimer = window.setTimeout(() => form.requestSubmit(), 0)
  }

  handleBeforeFrameRender (event) {
    const frame = event.target
    if (frame.id === 'registration_step') {
      this.stepSettled = false
      // Settles after turbo:frame-load; the fallback covers a render that never loads.
      window.clearTimeout(this.settleTimer)
      this.settleTimer = window.setTimeout(() => this.markStepSettled(), 1500)
    }
    this.initializeTimezone(event.detail.newFrame)
    const nextState = event.detail.newFrame?.querySelector('[data-registration-state]')?.dataset.registrationState
    if (frame.id !== 'registration_step' || !nextState) return

    // Turbo has already pushed the step's URL; the state follows at once so the
    // history check never sees ?step=verify on the email state.
    if (this.dataset.state === 'email' && nextState === 'verify' && this.transitionStep(event)) return

    this.dataset.state = nextState
  }

  // Email → verify runs as one View Transition, like the full-page load into
  // the profile step and Turbo page changes: the browser snapshots both
  // states, the logo and the card (email field → verification card) morph as
  // named elements, the dashboard sinks away, and everything else cross-fades.
  // Without the API (or with reduced motion) the step swaps instantly.
  //
  // Turbo's frame renderer waits for animation frames, which don't run inside a
  // View Transition's update callback, so the callback swaps the frame contents
  // itself and Turbo resumes afterwards with a render that is already done.
  transitionStep (event) {
    if (!document.startViewTransition || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false

    const frame = event.target
    const { newFrame, render, resume } = event.detail
    // Turbo copies the frame's old contents when its own render starts and puts
    // them back into the page it caches for Back; by then this callback has
    // already swapped them, so hand Turbo the email step's copy afterwards.
    const previousFrameElement = frame.cloneNode(true)
    let rendered = false
    const renderOnce = (currentElement, newElement) => {
      if (rendered) return

      rendered = true
      render(currentElement, newElement)
    }
    event.detail.render = renderOnce
    event.preventDefault()
    this.addEventListener('turbo:frame-render', () => {
      if (frame.delegate) frame.delegate.previousFrameElement = previousFrameElement
    }, { once: true })

    const transition = document.startViewTransition(() => {
      renderOnce(frame, newFrame)
      this.dataset.state = 'verify'
    })
    transition.updateCallbackDone.finally(resume)
    return true
  }

  handleFrameRender (event) {
    const renderedState = event.target.querySelector('[data-registration-state]')?.dataset.registrationState
    if (renderedState) this.dataset.state = renderedState
    this.classList.remove(...registrationTransientClasses)
    this.initializeState()
    this.saveHistorySnapshot(renderedState)
  }

  handleFrameLoad (event) {
    const state = event.target.querySelector('[data-registration-state]')?.dataset.registrationState
    if (state) this.dataset.state = state
    if (state === 'email') this.resetEmailSubmission()
    this.saveHistorySnapshot(state)
    this.initializeState()
    if (event.target.id === 'registration_step') this.settleStep()
  }

  // The step frame (data-turbo-action="advance") always finishes with a
  // URL-only visit; a code submitted before that visit renders can lose its
  // response to it. Turbo 7.3 starts the visit inside turbo:frame-load, Turbo 8
  // a few milliseconds later, so the step settles on the next turbo:load (or
  // after a short fallback), and a code completed in the meantime (fast
  // typing, autofill) is submitted then.
  settleStep () {
    let fallback = null
    const settle = () => {
      document.removeEventListener('turbo:load', settle)
      window.clearTimeout(fallback)
      this.markStepSettled()
    }

    document.addEventListener('turbo:load', settle)
    fallback = window.setTimeout(settle, 500)
  }

  markStepSettled () {
    window.clearTimeout(this.settleTimer)
    if (this.stepSettled || !this.isConnected) return

    this.stepSettled = true
    // Turbo 8's URL-only visit merges the page head, putting the landing title back.
    this.updateTitle()
    const input = this.querySelector('[data-registration-otp]')
    if (input) this.autoSubmitOtp(input, normalizeOtp(input.value))
  }

  handleSubmitEnd (event) {
    if (event.detail.success) {
      this.confirmVerification(event)
      return
    }

    // Unexpected failures answer with Turbo Streams, which never fire frame
    // load/render events, so the email submission must be released here.
    if (this.dataset.state === 'email') this.resetEmailSubmission()
    if (isUnhandledFailure(event.detail.fetchResponse)) this.showRecoveryAlert()
  }

  handleBeforeStreamRender (event) {
    if (event.target?.getAttribute?.('target') !== 'registration_step') return

    const render = event.detail.render
    event.detail.render = async (streamElement) => {
      await render(streamElement)
      if (!this.isConnected) return

      const state = this.querySelector('[data-registration-state]')?.dataset.registrationState
      if (state) this.dataset.state = state
      if (this.dataset.state === 'email') this.resetEmailSubmission()
      this.initializeState()
    }
  }

  handleFetchRequestError (event) {
    event.preventDefault()
    this.networkRecoveryError = event.detail.error
    window.clearTimeout(this.networkRecoveryTimer)
    this.networkRecoveryTimer = window.setTimeout(() => {
      this.networkRecoveryError = null
    }, 1000)
    this.resetEmailSubmission()
    this.showRecoveryAlert()
  }

  handleUnhandledRejection (event) {
    if (!this.networkRecoveryError || event.reason !== this.networkRecoveryError) return

    event.preventDefault()
    this.networkRecoveryError = null
    window.clearTimeout(this.networkRecoveryTimer)
  }

  handleFrameMissing (event) {
    if (event.target.id !== 'registration_step') return

    event.preventDefault()
    // A server error page (e.g. a static 500) keeps the current step and its
    // typed input; turbo:submit-end shows the toast for it.
    if (event.detail?.response?.status >= 400) {
      if (this.dataset.state === 'email') this.resetEmailSubmission()
      this.showRecoveryAlert()
      return
    }

    this.restoreEmailScreen()
    this.showRecoveryAlert()
  }

  handlePageShow () {
    this.resetEmailSubmission()
    this.resetProfileSubmission()
    this.scheduleHistoryRestore()
  }

  handleBeforeCache () {
    if (this.dataset.state === 'email') this.resetEmailSubmission()
  }

  handlePopState () {
    this.scheduleHistoryRestore()
  }

  handleVisibilityChange () {
    if (document.hidden) return

    this.scheduleHistoryRestore()
    this.revalidateVerificationFlow()
  }

  async revalidateVerificationFlow () {
    const url = new URL(window.location.href)
    if (this.dataset.state !== 'verify' || url.searchParams.get('step') !== 'verify' || this.verificationRevalidation) return

    const controller = new AbortController()
    this.verificationRevalidationController = controller
    this.verificationRevalidation = true

    try {
      const response = await window.fetch(this.dataset.statusUrl, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      })
      if (!response.ok) return

      const payload = await response.json()
      const destination = new URL(payload.url, window.location.origin)
      if (destination.pathname === url.pathname && destination.search === url.search) return

      window.location.replace(destination)
    } catch (_error) {
      // Keep the current form usable when this background check cannot connect.
    } finally {
      if (this.verificationRevalidationController === controller) {
        this.verificationRevalidationController = null
        this.verificationRevalidation = false
      }
    }
  }

  scheduleHistoryRestore () {
    window.clearTimeout(this.historyRestoreTimer)
    this.historyRestoreTimer = window.setTimeout(() => this.restoreHistoryState(), 100)
  }

  restoreHistoryState () {
    // Mid-render the URL can be ahead of the step; check again once it settles.
    if (!this.stepSettled) {
      this.scheduleHistoryRestore()
      return
    }

    const step = new URL(window.location.href).searchParams.get('step') || 'email'
    if (this.dataset.state === step) {
      window.sessionStorage.removeItem(registrationHistoryReloadKey)
      return
    }

    if (step !== 'email') {
      if (window.sessionStorage.getItem(registrationHistoryReloadKey) === window.location.href) {
        const fallbackUrl = new URL(window.location.href)
        fallbackUrl.searchParams.delete('step')
        window.history.replaceState(window.history.state, '', fallbackUrl)
        window.sessionStorage.removeItem(registrationHistoryReloadKey)
        return
      }

      window.sessionStorage.setItem(registrationHistoryReloadKey, window.location.href)
      window.location.reload()
      return
    }

    const snapshot = registrationHistorySnapshots.get(step)
    if (!snapshot) return

    this.dataset.state = step
    this.innerHTML = snapshot
    if (step === 'email') this.resetEmailSubmission()
    this.initializeState()
  }

  restoreEmailScreen () {
    const url = new URL(window.location.href)
    url.searchParams.delete('step')
    url.searchParams.delete('email')

    const snapshot = registrationHistorySnapshots.get('email')
    if (!snapshot) {
      window.location.replace(url)
      return
    }

    window.history.replaceState(window.history.state, '', url)
    this.dataset.state = 'email'
    this.innerHTML = snapshot
    this.resetEmailSubmission()
    this.initializeState()
    this.saveHistorySnapshot('email')
  }

  saveHistorySnapshot (state) {
    if (!state) return

    registrationHistorySnapshots.set(state, this.innerHTML)
  }

  resetTransientState () {
    if (this.dataset.state === 'email') {
      this.resetEmailSubmission()
      return
    }

    this.classList.remove(...registrationTransientClasses)
  }

  resetEmailSubmission () {
    window.clearTimeout(this.emailSubmitTimer)
    this.classList.remove(...registrationTransientClasses)
    const form = this.querySelector('#registration-form')
    if (form) {
      delete form.dataset.sending
      form.removeAttribute('aria-busy')
      form.querySelectorAll('[type="submit"]').forEach((submitter) => {
        const label = submitter.dataset.originalLabel ||
          (('value' in submitter ? submitter.value : submitter.textContent) === registrationSendingLabel ? 'Start free' : null)
        if (label) {
          if ('value' in submitter) submitter.value = label
          else submitter.textContent = label
        }
        delete submitter.dataset.originalLabel
        submitter.disabled = false
      })
    }
  }

  confirmVerification (event) {
    const form = event.target
    if (!form?.querySelector?.('input[name="step"][value="verify"]')) return

    const location = event.detail.fetchResponse?.location
    if (!location || new URL(location).searchParams.get('step') !== 'complete') return

    const submitter = event.detail.formSubmission?.submitter || form.querySelector('[type="submit"]')
    if (!submitter) return

    if ('value' in submitter) submitter.value = registrationVerifiedLabel
    else submitter.textContent = registrationVerifiedLabel
    submitter.disabled = true
    submitter.dataset.registrationVerified = 'true'
  }

  showProfileSubmission (form, submitter = form.querySelector('[type="submit"]')) {
    if (!submitter || form.dataset.submitting === 'true') return

    form.dataset.submitting = 'true'
    form.setAttribute('aria-busy', 'true')
    submitter.dataset.originalLabel = submitter.value || submitter.textContent
    // Defer so the native submission is already under way before the
    // button changes; the submitter's own name/value is not needed.
    window.setTimeout(() => {
      if ('value' in submitter) submitter.value = registrationCreatingLabel
      else submitter.textContent = registrationCreatingLabel
      submitter.disabled = true
    }, 0)
  }

  resetProfileSubmission () {
    const form = this.querySelector('.registration-complete-form')
    if (!form || form.dataset.submitting !== 'true') return

    delete form.dataset.submitting
    form.removeAttribute('aria-busy')
    const submitter = form.querySelector('[type="submit"]')
    if (!submitter) return

    if (submitter.dataset.originalLabel) {
      if ('value' in submitter) submitter.value = submitter.dataset.originalLabel
      else submitter.textContent = submitter.dataset.originalLabel
      delete submitter.dataset.originalLabel
    }
    submitter.disabled = false
  }

  showRecoveryAlert () {
    // One toast at a time: reuse the single #flash container and replace its contents.
    let container = document.getElementById('flash')
    if (!container) {
      container = document.createElement('div')
      container.id = 'flash'
      document.body.appendChild(container)
    }
    container.className = 'pointer-events-none fixed left-3 right-3 top-3 z-[1000] md:left-auto'

    const message = document.createElement('flash-message')
    message.dataset.duration = '5000'
    message.dataset.registrationRecoveryAlert = 'true'
    message.className = 'pointer-events-auto relative ml-auto flex w-fit max-w-full items-center gap-2.5 rounded-xl border border-base-300 bg-base-100 px-3 py-2.5 text-base-content shadow-lg'
    message.setAttribute('role', 'alert')
    message.setAttribute('aria-live', 'assertive')

    const text = document.createElement('span')
    text.className = 'min-w-0 text-sm'
    text.textContent = registrationRecoveryMessage
    message.appendChild(text)

    const close = document.createElement('button')
    close.type = 'button'
    close.dataset.flashClose = 'true'
    close.className = 'flex h-7 w-7 flex-none items-center justify-center rounded-lg text-base-content/60'
    close.setAttribute('aria-label', 'Close')
    close.textContent = '×'
    message.appendChild(close)

    container.replaceChildren(message)
  }

  initializeState () {
    this.updateTitle()
    this.initializeTimezone()
    this.initializeOtp()
    this.initializeCountry()
    this.initializePasswordToggle()
    this.initializeHeardAbout()
    this.initializeResendFeedback()
    this.focusStateTarget()
    this.captureSafeEvent()
  }

  initializeTimezone (root = this) {
    const input = root?.querySelector('input[name="timezone"]')
    if (!input || input.value) return

    input.value = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  }

  initializeOtp () {
    const input = this.querySelector('[data-registration-otp]')
    if (!input || input.dataset.initialized) return

    input.dataset.initialized = 'true'
    const submitter = input.form?.querySelector('[type="submit"]')
    if (submitter && !submitter.hasAttribute('data-turbo-submits-with')) {
      submitter.setAttribute('data-turbo-submits-with', registrationVerifyingLabel)
    }
    input.addEventListener('beforeinput', (event) => {
      if (!event.inputType?.startsWith('insert') || event.data == null) return

      const selected = (input.selectionEnd ?? 0) - (input.selectionStart ?? 0)
      const room = registrationOtpLength - normalizeOtp(input.value).length + Math.max(selected, 0)
      if (!/\d/.test(event.data) || room <= 0) event.preventDefault()
    })
    input.addEventListener('paste', (event) => {
      const code = normalizeOtp(event.clipboardData?.getData('text'))
      if (!code) return

      event.preventDefault()
      input.value = code
      input.dispatchEvent(new Event('input', { bubbles: true }))
    })
  }

  // Delegated so a code typed or autofilled before turbo:frame-render (which
  // initializes the step) is still cleaned up and submitted.
  handleInput (event) {
    const input = event.target
    if (!input.matches?.('[data-registration-otp]')) return

    const code = normalizeOtp(input.value)
    if (input.value !== code) input.value = code
    this.autoSubmitOtp(input, code)
  }

  // The last digit (typed, pasted or autofilled) submits the code; the button stays for retries.
  // A code is sent once, so a rejected code only resubmits after it is edited.
  autoSubmitOtp (input, code) {
    if (code.length < registrationOtpLength) {
      delete input.dataset.autoSubmittedCode
      return
    }
    // A re-rendered field that already holds the rejected code doesn't resubmit it.
    if (input.getAttribute('aria-invalid') === 'true' && input.defaultValue === code) return
    if (!this.stepSettled || input.dataset.autoSubmittedCode === code || !input.form) return

    const submitter = input.form.querySelector('[type="submit"]')
    if (submitter?.disabled) return

    input.dataset.autoSubmittedCode = code
    input.form.requestSubmit(submitter)
  }

  updateTitle () {
    document.title = registrationStepTitles[this.dataset.state] || this.landingTitle
  }

  initializeHeardAbout () {
    const fieldset = this.querySelector('[data-registration-heard-about]')
    if (!fieldset || fieldset.dataset.initialized) return

    fieldset.dataset.initialized = 'true'
    fieldset.addEventListener('change', (event) => {
      if (event.target.name !== 'heard_about' || event.target.value !== 'other') return

      fieldset.querySelector('input[name="heard_about_detail"]')?.focus()
    })
  }

  initializeCountry () {
    const select = this.querySelector('[data-registration-country]')
    if (!select || select.dataset.initialized) return

    select.dataset.initialized = 'true'
    if (select.value || select.getAttribute('aria-invalid') === 'true' || !this.dataset.countryHint) return

    const optionExists = Array.from(select.options).some((option) => option.value === this.dataset.countryHint)
    if (optionExists) select.value = this.dataset.countryHint
  }

  initializePasswordToggle () {
    const button = this.querySelector('[data-password-toggle]')
    const input = this.querySelector('[data-registration-password]')
    if (!button || !input || button.dataset.initialized) return

    button.dataset.initialized = 'true'
    button.addEventListener('click', () => {
      const isVisible = input.type === 'text'
      input.type = isVisible ? 'password' : 'text'
      button.textContent = isVisible ? 'Show' : 'Hide'
      button.setAttribute('aria-pressed', String(!isVisible))
    })
  }

  initializeResendFeedback () {
    const button = this.querySelector('[data-registration-resend]')
    if (!button || button.dataset.initialized) return

    button.dataset.initialized = 'true'
    button.addEventListener('click', () => {
      button.value = 'Sending…'
      button.setAttribute('aria-live', 'polite')
    })

    this.resendCountdown?.stop()
    const state = button.closest('[data-registration-resend-state]')
    const countdown = state?.querySelector('[data-registration-resend-countdown]')
    const readyStatus = state?.querySelector('[data-registration-resend-ready]')
    const availableAt = Number(state?.dataset.resendAvailableAt)
    if (!availableAt) return

    this.resendCountdown = new RegistrationResendCountdown({
      availableAt,
      ...createRegistrationResendHandlers({
        button,
        countdown,
        readyStatus,
        limitReached: state.dataset.resendMode === 'limit_reached'
      })
    })
    this.resendCountdown.start()
  }

  focusStateTarget () {
    let invalidField = this.querySelector('[aria-invalid="true"], .field_with_errors input, .field_with_errors select')
    // An invalid choice group (the pills) focuses its first option.
    if (invalidField?.matches('fieldset')) invalidField = invalidField.querySelector('input')
    if (invalidField) {
      this.focusElement(invalidField)
      return
    }

    if (this.dataset.state === 'verify') {
      const otp = this.querySelector('[data-registration-otp]')
      if (otp) {
        this.focusElement(otp)
        return
      }
    }

    const heading = this.querySelector('[data-registration-heading]')
    if (!heading) return

    heading.setAttribute('tabindex', '-1')
    this.focusElement(heading)
  }

  focusElement (element) {
    if (document.activeElement === element) return

    element.focus({ preventScroll: true })
  }

  captureSafeEvent () {
    const state = this.dataset.state || 'email'
    if (this.trackedStates.has(state)) return

    this.trackedStates.add(state)
    window.posthog?.capture('registration_step_viewed', {
      step: state,
      country_hint: this.dataset.countryHint || null,
      mobile: window.matchMedia('(max-width: 640px)').matches
    })
  }
}
